import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { api } from "../../../../../convex/_generated/api";
import { LEAD_SOURCES } from "@/lib/leadSources";

interface CreateLeadSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (lead: any) => void;
}

export function CreateLeadSheet({ isOpen, onClose, onAdd }: CreateLeadSheetProps) {
  const [showCustomAccountType, setShowCustomAccountType] = useState(false);
  const [customAccountType, setCustomAccountType] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    accountType: "",
    estimatedValue: "",
    website: "",
    status: "new",
    source: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    contactTitle: "",
    // Opportunity fields
    opportunityDescription: "",
    budgetMin: "",
    budgetMax: "",
    employeeCount: "",
    frequency: "",
    potentialStartDate: "",
    estimatedSquareFootage: "",
    requirements: "",
  });
  const [isGeocoding, setIsGeocoding] = useState(false);

  const geocodingLibrary = useMapsLibrary("geocoding");
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (!geocodingLibrary) return;
    setGeocoder(new geocodingLibrary.Geocoder());
  }, [geocodingLibrary]);

  const createLead = useMutation(api.businesses.create);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, "");

    // Format based on length
    if (phoneNumber.length === 0) return "";
    if (phoneNumber.length <= 3) return phoneNumber;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const formattedPhone = formatPhoneNumber(value);
    setFormData((prev) => ({
      ...prev,
      [id]: formattedPhone,
    }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsGeocoding(true);
      let coordinates = undefined;

      if (formData.address && geocoder) {
        try {
          const result = await new Promise<google.maps.GeocoderResult[] | null>((resolve) => {
            geocoder.geocode({ address: formData.address }, (results, status) => {
              if (status === "OK" && results) {
                resolve(results);
              } else {
                console.error("Geocoding failed:", status);
                resolve(null);
              }
            });
          });

          if (result && result[0]?.geometry?.location) {
            coordinates = {
              lat: result[0].geometry.location.lat(),
              lng: result[0].geometry.location.lng(),
            };
          }
        } catch (error) {
          console.error("Geocoding error:", error);
        }
      }

      // Use the actual account type if custom is selected
      const finalAccountType = showCustomAccountType ? customAccountType : formData.accountType;

      // Instead of creating the lead here, we'll pass the data to the parent component
      // to handle the creation to avoid duplicate records

      // Parse requirements from textarea
      const requirementsArray = formData.requirements
        ? formData.requirements.split("\n").filter((req) => req.trim() !== "")
        : [];

      // Call the onAdd callback with the form data
      onAdd({
        ...formData,
        coordinates,
        accountType: finalAccountType,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        contactTitle: formData.contactTitle,
        // Opportunity fields
        opportunityDescription: formData.opportunityDescription || undefined,
        budgetMin: formData.budgetMin || undefined,
        budgetMax: formData.budgetMax || undefined,
        employeeCount: formData.employeeCount || undefined,
        frequency: formData.frequency || undefined,
        potentialStartDate: formData.potentialStartDate || undefined,
        estimatedSquareFootage: formData.estimatedSquareFootage || undefined,
        requirements: requirementsArray.length > 0 ? requirementsArray : undefined,
      });

      // Reset form data
      setFormData({
        name: "",
        address: "",
        accountType: "",
        estimatedValue: "",
        website: "",
        status: "new",
        source: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        contactTitle: "",
        // Opportunity fields
        opportunityDescription: "",
        budgetMin: "",
        budgetMax: "",
        employeeCount: "",
        frequency: "",
        potentialStartDate: "",
        estimatedSquareFootage: "",
        requirements: "",
      });
      setCustomAccountType("");
      setShowCustomAccountType(false);

      // Close the sheet after successful submission
      onClose();
    } catch (error) {
      console.error("Error creating lead:", error);
    } finally {
      setIsGeocoding(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose} modal={true}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Add New Lead</SheetTitle>
          <SheetDescription>Enter the details for the new potential client.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                placeholder="Enter company name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Location</Label>
              <Input
                id="address"
                placeholder="Enter physical address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountType">Account Type</Label>
              <Select
                onValueChange={(value) => {
                  setShowCustomAccountType(value === "custom");
                  handleSelectChange("accountType", value);
                }}
                value={formData.accountType || undefined}
                defaultValue={formData.accountType}
              >
                <SelectTrigger id="accountType">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectGroup>
                    <SelectLabel>Commercial Office Building</SelectLabel>
                    <SelectItem value="corporate-office">Corporate office</SelectItem>
                    <SelectItem value="professional-service">Professional service firm</SelectItem>
                    <SelectItem value="co-working">Co-working space</SelectItem>
                    <SelectItem value="business-park">Business park</SelectItem>
                    <SelectItem value="office-complex">Office complex</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Educational Institution</SelectLabel>
                    <SelectItem value="k-12">K-12 school</SelectItem>
                    <SelectItem value="university">University or college</SelectItem>
                    <SelectItem value="private-academy">Private academy</SelectItem>
                    <SelectItem value="vocational-school">Vocational school</SelectItem>
                    <SelectItem value="daycare">Daycare center</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Healthcare Facility</SelectLabel>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="medical-clinic">Medical clinic</SelectItem>
                    <SelectItem value="dental-office">Dental office</SelectItem>
                    <SelectItem value="surgery-center">Outpatient surgery center</SelectItem>
                    <SelectItem value="physical-therapy">Physical therapy center</SelectItem>
                    <SelectItem value="long-term-care">Long-term care facility</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Retail Space</SelectLabel>
                    <SelectItem value="shopping-mall">Shopping mall</SelectItem>
                    <SelectItem value="department-store">Department store</SelectItem>
                    <SelectItem value="boutique">Boutique</SelectItem>
                    <SelectItem value="grocery-store">Grocery store</SelectItem>
                    <SelectItem value="big-box">Big box retailer</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Hospitality Industry</SelectLabel>
                    <SelectItem value="hotel">Hotel or motel</SelectItem>
                    <SelectItem value="resort">Resort</SelectItem>
                    <SelectItem value="convention-center">Convention center</SelectItem>
                    <SelectItem value="banquet-hall">Banquet hall</SelectItem>
                    <SelectItem value="restaurant">Restaurant or cafe</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Financial Institution</SelectLabel>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="credit-union">Credit union</SelectItem>
                    <SelectItem value="investment-firm">Investment firm</SelectItem>
                    <SelectItem value="insurance-company">Insurance company</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Industrial Facility</SelectLabel>
                    <SelectItem value="manufacturing">Manufacturing plant</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="distribution-center">Distribution center</SelectItem>
                    <SelectItem value="food-processing">Food processing facility</SelectItem>
                    <SelectItem value="factory">Factory</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Religious Facility</SelectLabel>
                    <SelectItem value="church">Church</SelectItem>
                    <SelectItem value="synagogue">Synagogue</SelectItem>
                    <SelectItem value="mosque">Mosque</SelectItem>
                    <SelectItem value="religious-education">Religious education center</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Government Building</SelectLabel>
                    <SelectItem value="municipal-office">Municipal office</SelectItem>
                    <SelectItem value="courthouse">Courthouse</SelectItem>
                    <SelectItem value="post-office">Post office</SelectItem>
                    <SelectItem value="dmv">DMV location</SelectItem>
                    <SelectItem value="public-library">Public library</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Entertainment Venue</SelectLabel>
                    <SelectItem value="movie-theater">Movie theater</SelectItem>
                    <SelectItem value="concert-hall">Concert hall</SelectItem>
                    <SelectItem value="sports-arena">Sports arena</SelectItem>
                    <SelectItem value="museum">Museum</SelectItem>
                    <SelectItem value="art-gallery">Art gallery</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Transportation Hub</SelectLabel>
                    <SelectItem value="airport">Airport</SelectItem>
                    <SelectItem value="bus-terminal">Bus terminal</SelectItem>
                    <SelectItem value="train-station">Train station</SelectItem>
                    <SelectItem value="subway-station">Subway station</SelectItem>
                  </SelectGroup>

                  <SelectGroup>
                    <SelectLabel>Residential Property</SelectLabel>
                    <SelectItem value="apartment-complex">Apartment complex</SelectItem>
                    <SelectItem value="condominium">Condominium</SelectItem>
                    <SelectItem value="homeowners-association">Homeowners association</SelectItem>
                  </SelectGroup>

                  <SelectItem value="custom">Custom Account Type</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {showCustomAccountType && (
              <div className="space-y-2">
                <Label htmlFor="custom-account-type">Custom Account Type</Label>
                <Input
                  id="custom-account-type"
                  placeholder="Enter custom account type"
                  value={customAccountType}
                  onChange={(e) => setCustomAccountType(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="estimatedValue">Estimated Contract Value</Label>
              <Input
                id="estimatedValue"
                placeholder="Enter estimated value"
                type="number"
                value={formData.estimatedValue}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                placeholder="Company website URL"
                value={formData.website}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                onValueChange={(value) => handleSelectChange("status", value)}
                value={formData.status || undefined}
                defaultValue={formData.status}
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="unqualified">Unqualified</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="source">Lead Source</Label>
              <Select
                onValueChange={(value) => handleSelectChange("source", value)}
                value={formData.source || undefined}
                defaultValue={formData.source}
              >
                <SelectTrigger id="source">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((source) => (
                    <SelectItem key={source.value} value={source.value}>
                      {source.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-6 border-t border-gray-200 py-4">
              <h3 className="text-lg font-medium">Primary Contact Information</h3>
              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  placeholder="Primary contact name"
                  value={formData.contactName}
                  onChange={handleInputChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactEmail">Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  placeholder="Contact email address"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactPhone">Phone</Label>
                <Input
                  id="contactPhone"
                  placeholder="(123) 456-7890"
                  value={formData.contactPhone}
                  onChange={handlePhoneChange}
                  maxLength={14}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactTitle">Job Title</Label>
                <Input
                  id="contactTitle"
                  placeholder="Contact job title"
                  value={formData.contactTitle}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Opportunity Section */}
            <div className="space-y-4">
              <h3 className="border-b pb-2 text-lg font-semibold">Opportunity</h3>

              <div className="space-y-2">
                <Label htmlFor="opportunityDescription">Description</Label>
                <Textarea
                  id="opportunityDescription"
                  placeholder="Enter opportunity description"
                  rows={3}
                  value={formData.opportunityDescription}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="budgetMin">Budget Min</Label>
                  <Input
                    id="budgetMin"
                    placeholder="e.g., 5000"
                    value={formData.budgetMin}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budgetMax">Budget Max</Label>
                  <Input
                    id="budgetMax"
                    placeholder="e.g., 7000"
                    value={formData.budgetMax}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employeeCount">Employee Count</Label>
                  <Input
                    id="employeeCount"
                    placeholder="e.g., 150"
                    value={formData.employeeCount}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequency</Label>
                  <Select
                    value={formData.frequency}
                    onValueChange={(value) => {
                      setFormData((prev) => ({
                        ...prev,
                        frequency: value,
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1_per_week">1 time per week</SelectItem>
                      <SelectItem value="2_per_week">2 times per week</SelectItem>
                      <SelectItem value="3_per_week">3 times per week</SelectItem>
                      <SelectItem value="4_per_week">4 times per week</SelectItem>
                      <SelectItem value="5_per_week">5 times per week</SelectItem>
                      <SelectItem value="6_per_week">6 times per week</SelectItem>
                      <SelectItem value="7_per_week">7 times per week</SelectItem>
                      <SelectItem value="1_per_month">1 time per month</SelectItem>
                      <SelectItem value="2_per_month">2 times per month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="potentialStartDate">Potential Start Date</Label>
                <Input
                  id="potentialStartDate"
                  type="date"
                  value={formData.potentialStartDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimatedSquareFootage">Estimated Square Footage</Label>
                <Input
                  id="estimatedSquareFootage"
                  placeholder="e.g., 25000"
                  value={formData.estimatedSquareFootage}
                  onChange={handleInputChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requirements">Requirements</Label>
                <Textarea
                  id="requirements"
                  placeholder="Enter requirements (one per line)"
                  rows={4}
                  value={formData.requirements}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <Button className="w-full" onClick={handleSubmit} disabled={isGeocoding}>
              {isGeocoding ? "Geocoding..." : "Add Lead"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
